import { useId } from 'react';
import type { ItemInput } from '@tahi/core';
import { minorToInput, parsePesoInput } from '../../budget/format';
import { copy } from '../copy';
import { numberText, parseQuantity } from '../format';
import { INPUT } from '../styles';

/** What the item form holds while the user types (all text, parsed on save). */
export interface ItemDraft {
  name: string;
  quantity: string;
  unit: string;
  estimate: string;
  store: string;
}

export const EMPTY_DRAFT: ItemDraft = { name: '', quantity: '', unit: '', estimate: '', store: '' };

export function draftOf(i: {
  name: string;
  quantity: number | null;
  unit: string | null;
  estimateMinor: number | null;
  store: string | null;
}): ItemDraft {
  return {
    name: i.name,
    quantity: i.quantity == null ? '' : numberText(i.quantity),
    unit: i.unit ?? '',
    estimate: i.estimateMinor == null ? '' : minorToInput(i.estimateMinor),
    store: i.store ?? '',
  };
}

/** Parses a draft; a field that doesn't read as a number gives an error key instead. */
export function parseDraft(
  d: ItemDraft,
): { ok: true; input: ItemInput } | { ok: false; error: string } {
  const quantity = d.quantity.trim() === '' ? null : parseQuantity(d.quantity);
  if (d.quantity.trim() !== '' && quantity === null) return { ok: false, error: 'bad_quantity' };
  const estimateMinor = d.estimate.trim() === '' ? null : parsePesoInput(d.estimate);
  if (d.estimate.trim() !== '' && estimateMinor === null)
    return { ok: false, error: 'bad_estimate' };
  return {
    ok: true,
    input: { name: d.name, quantity, unit: d.unit, estimateMinor, store: d.store },
  };
}

/** Quantity, unit, estimated price and store: the optional fields of an item. */
export function ItemDetailFields({
  draft,
  onChange,
  estimateHint,
}: {
  draft: ItemDraft;
  onChange(patch: Partial<ItemDraft>): void;
  estimateHint?: string | null;
}) {
  const id = useId();
  const field = 'flex min-w-0 flex-col gap-1 text-sm text-muted';
  return (
    <div className="grid grid-cols-2 gap-3">
      <label className={field}>
        {copy.quantity}
        <input
          inputMode="decimal"
          value={draft.quantity}
          onChange={(e) => onChange({ quantity: e.target.value })}
          className={`${INPUT} w-full`}
        />
      </label>
      <label className={field}>
        {copy.unit}
        <input
          value={draft.unit}
          maxLength={20}
          placeholder={copy.unitPlaceholder}
          onChange={(e) => onChange({ unit: e.target.value })}
          className={`${INPUT} w-full`}
        />
      </label>
      <label className={`${field} col-span-2`}>
        {copy.estimate}
        <span className="relative flex items-center">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 font-mono text-muted"
          >
            ₱
          </span>
          <input
            aria-label={copy.estimate}
            inputMode="decimal"
            value={draft.estimate}
            aria-describedby={estimateHint ? `${id}-hint` : undefined}
            onChange={(e) => onChange({ estimate: e.target.value })}
            className={`${INPUT} w-full pl-7 font-mono tabular-nums`}
          />
        </span>
        {estimateHint ? (
          <span id={`${id}-hint`} className="text-xs">
            {estimateHint}
          </span>
        ) : null}
      </label>
      <label className={`${field} col-span-2`}>
        {copy.store}
        <input
          value={draft.store}
          maxLength={60}
          onChange={(e) => onChange({ store: e.target.value })}
          className={`${INPUT} w-full`}
        />
      </label>
    </div>
  );
}
